import _extends from "@babel/runtime/helpers/extends";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import AKInlineEdit from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useSmartLinkReload } from '@atlaskit/smart-card/hooks';
import { useDatasourceAnalyticsEvents } from '../../../analytics';
import { startUfoExperience } from '../../../analytics/ufoExperiences';
import { useDatasourceExperienceId } from '../../../contexts/datasource-experience-id';
import { useDatasourceTableFlag } from '../../../hooks/useDatasourceTableFlag';
import { useDatasourceActions, useDatasourceItem } from '../../../state';
import { editType } from '../edit-type';
import { EmptyAvatar } from '../shared-components/avatar';
import { getFieldLabelById } from '../utils';
import { tableCellMessages } from './messages';
export var InlineEditUFOExperience = 'inline-edit-rendered';
var editContainerStyles = xcss({
  marginBlockStart: 'space.negative.100'
});
/**
 * @returns String of the new field value, or ID of status transition / atlassian user ID / priority ID.
 * @throws Error if the value is not supplied.
 */
export var newGetBackendUpdateValue = function newGetBackendUpdateValue(typedNewValue) {
  if (typedNewValue.values.length === 0) {
    throw new Error("Datasource 2 way sync: Backend update value or value ID not supplied for type ".concat(typedNewValue.type));
  }
  switch (typedNewValue.type) {
    case 'string':
      return typedNewValue.values[0];
    case 'status':
      var transitionId = typedNewValue.values[0].transitionId;
      if (transitionId === undefined || transitionId === '') {
        throw new Error("Datasource 2 way sync: Backend status transition ID not supplied for type transition");
      }
      return transitionId;
    case 'user':
      var atlassianUserId = typedNewValue.values[0].atlassianUserId;
      if (atlassianUserId === undefined || atlassianUserId === '') {
        throw new Error("Datasource 2 way sync: Backend atlasian user ID not supplied for type user");
      }
      return atlassianUserId;
    case 'icon':
      var id = typedNewValue.values[0].id;
      if (id === undefined || id === '') {
        throw new Error("Datasource 2 way sync: Backend update ID not supplied for type icon");
      }
      return id;
  }
  throw new Error("Datasource 2 way sync Backend update value not implemented for type ".concat(typedNewValue.type));
};
var getBackendUpdateValue = function getBackendUpdateValue(typedNewValue) {
  var _typedNewValue$values, _typedNewValue$values2, _typedNewValue$values3;
  switch (typedNewValue.type) {
    case 'string':
      return typedNewValue.values[0] || '';
    case 'status':
      return ((_typedNewValue$values = typedNewValue.values[0]) === null || _typedNewValue$values === void 0 ? void 0 : _typedNewValue$values.transitionId) || '';
    case 'user':
      return ((_typedNewValue$values2 = typedNewValue.values[0]) === null || _typedNewValue$values2 === void 0 ? void 0 : _typedNewValue$values2.atlassianUserId) || '';
    case 'icon':
      return ((_typedNewValue$values3 = typedNewValue.values[0]) === null || _typedNewValue$values3 === void 0 ? void 0 : _typedNewValue$values3.id) || '';
  }
  throw new Error("Datasource 2 way sync Backend update value not implemented for type ".concat(typedNewValue.type));
};
var getCurrentFieldLabel = function getCurrentFieldLabel(typedNewValue) {
  var _typedNewValue$values4, _typedNewValue$values5, _typedNewValue$values6;
  switch (typedNewValue.type) {
    case 'string':
      return typedNewValue.values[0] || '';
    case 'status':
      return ((_typedNewValue$values4 = typedNewValue.values[0]) === null || _typedNewValue$values4 === void 0 ? void 0 : _typedNewValue$values4.text) || '';
    case 'user':
      return ((_typedNewValue$values5 = typedNewValue.values[0]) === null || _typedNewValue$values5 === void 0 ? void 0 : _typedNewValue$values5.displayName) || '';
    case 'icon':
      return ((_typedNewValue$values6 = typedNewValue.values[0]) === null || _typedNewValue$values6 === void 0 ? void 0 : _typedNewValue$values6.text) || '';
    default:
      return '';
  }
};
var mapUpdatedItem = function mapUpdatedItem(existingItem, columnKey, newValue) {
  switch (newValue.type) {
    case 'string':
      return _objectSpread(_objectSpread({}, existingItem), {}, _defineProperty({}, columnKey, {
        data: newValue.values[0] || ''
      }));
    case 'status':
    case 'user':
    case 'icon':
      return newValue.values[0] ? _objectSpread(_objectSpread({}, existingItem), {}, _defineProperty({}, columnKey, {
        data: newValue.values[0]
      })) : existingItem;
    default:
  }
  return null;
};
var isNewValue = function isNewValue(columnKey, newItem, existingData) {
  var _newItem$columnKey, _existingData$columnK;
  return ((_newItem$columnKey = newItem[columnKey]) === null || _newItem$columnKey === void 0 ? void 0 : _newItem$columnKey.data) && (!((_existingData$columnK = existingData[columnKey]) !== null && _existingData$columnK !== void 0 && _existingData$columnK.data) || newItem[columnKey].data !== existingData[columnKey].data);
};
var useRefreshDatasourceItem = function useRefreshDatasourceItem(item) {
  var _item$data;
  var url = item === null || item === void 0 || (_item$data = item.data) === null || _item$data === void 0 || (_item$data = _item$data.key) === null || _item$data === void 0 || (_item$data = _item$data.data) === null || _item$data === void 0 ? void 0 : _item$data.url;

  // passing empty string to the hook isn't ideal, but the alternatives are too much effort for this small fix.
  var reloadSmartLinkAction = useSmartLinkReload({
    url: url || ''
  });
  return useCallback(function () {
    if (url) {
      reloadSmartLinkAction();
    }
  }, [reloadSmartLinkAction, url]);
};
export var InlineEditOld = function InlineEditOld(_ref) {
  var ari = _ref.ari,
    execute = _ref.execute,
    executeFetch = _ref.executeFetch,
    readView = _ref.readView,
    columnKey = _ref.columnKey,
    columnTitle = _ref.columnTitle,
    datasourceTypeWithValues = _ref.datasourceTypeWithValues;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isEditing = _useState2[0],
    setIsEditing = _useState2[1];
  var _useState3 = useState(datasourceTypeWithValues),
    _useState4 = _slicedToArray(_useState3, 2),
    editValues = _useState4[0],
    setEditValues = _useState4[1];
  var item = useDatasourceItem({
    id: ari
  });
  var _ref2 = item || {},
    entityType = _ref2.entityType,
    integrationKey = _ref2.integrationKey;
  var _useDatasourceTableFl = useDatasourceTableFlag(),
    showErrorFlag = _useDatasourceTableFl.showErrorFlag;
  var _useDatasourceActions = useDatasourceActions(),
    onUpdateItem = _useDatasourceActions.onUpdateItem;
  var _useDatasourceAnalyti = useDatasourceAnalyticsEvents(),
    fireEvent = _useDatasourceAnalyti.fireEvent;
  var refreshDatasourceItem = useRefreshDatasourceItem(item);
  var experienceId = useDatasourceExperienceId();
  var onCommitUpdate = useCallback(function (newValue) {
    if (!item) {
      setIsEditing(false);
      return;
    }
    var existingData = item.data;
    var newItem = mapUpdatedItem(item.data, columnKey, newValue);
    if (!newItem || !isNewValue(columnKey, newItem, existingData)) {
      setIsEditing(false);
      return;
    }
    onUpdateItem(ari, newItem);
    fireEvent('ui.form.submitted.inlineEdit', {});
    var updateValue;
    if (fg('platform-datasources-inline-edit-id-checks')) {
      try {
        // TODO: Refactor types so that valid update values are guaranteed for
        // all object types. Invalid options should be filtered out of options -
        // this frontend error flag is a last resort.
        updateValue = newGetBackendUpdateValue(newValue);
      } catch (_unused) {
        // Show an error as the new value that was going to be sent to the
        // backend is invalid (and would have failed anyway, silently to the user)
        showErrorFlag({});
        onUpdateItem(ari, existingData);
        setIsEditing(false);
        return;
      }
    }
    execute(updateValue !== undefined && fg('platform-datasources-inline-edit-id-checks') ? updateValue :
    // Old behaviour is preserved in non-FFed path: errors thrown by getBackendUpdateValue are caught by
    // the error boundary, _not_ by the catch block & frontend flag here.
    getBackendUpdateValue(newValue)).then(refreshDatasourceItem).catch(function (error) {
      var status = error && _typeof(error) === 'object' ? error.status : undefined;
      showErrorFlag({
        status: status
      });
      onUpdateItem(ari, existingData);
    });
    setIsEditing(false);
  }, [item, columnKey, onUpdateItem, ari, refreshDatasourceItem, fireEvent, execute, showErrorFlag]);
  var onEdit = useCallback(function () {
    setIsEditing(true);
    if (experienceId) {
      startUfoExperience({
        name: InlineEditUFOExperience
      }, experienceId);
    }
    if (integrationKey && entityType) {
      fireEvent('ui.inlineEdit.clicked.datasource', {
        integrationKey: integrationKey,
        entityType: entityType,
        fieldKey: columnKey
      });
    }
  }, [columnKey, entityType, experienceId, fireEvent, integrationKey]);
  var onCancelEdit = useCallback(function () {
    setIsEditing(false);
    if (integrationKey && entityType) {
      fireEvent('ui.inlineEdit.cancelled.datasource', {
        integrationKey: integrationKey,
        entityType: entityType,
        fieldKey: columnKey
      });
    }
  }, [columnKey, entityType, fireEvent, integrationKey]);
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var editButtonLabel = useMemo(function () {
    return formatMessage(tableCellMessages.editButtonLabel, {
      fieldName: columnTitle,
      fieldValue: getCurrentFieldLabel(editValues)
    });
  }, [columnTitle, formatMessage, editValues]);
  return /*#__PURE__*/React.createElement(Box, {
    xcss: editContainerStyles
  }, /*#__PURE__*/React.createElement(AKInlineEdit, _extends({}, editType({
    defaultValue: datasourceTypeWithValues,
    currentValue: editValues,
    setEditValues: setEditValues,
    executeFetch: executeFetch,
    labelId: getFieldLabelById(columnKey)
  }), {
    hideActionButtons: true,
    readView: editableRenderType({
      defaultValue: datasourceTypeWithValues,
      readView: readView
    }),
    readViewFitContainerWidth: true,
    isEditing: isEditing,
    onEdit: onEdit,
    onCancel: onCancelEdit,
    onConfirm: function onConfirm() {
      return onCommitUpdate(editValues);
    },
    editButtonLabel: editButtonLabel
  })));
};

/**
 *
 * This function allows us to manipulate the readView on editable cells.
 * This way, for example, we can show a fallback Avatar on empty user cells.
 *
 */
var editableRenderType = function editableRenderType(_ref3) {
  var defaultValue = _ref3.defaultValue,
    readView = _ref3.readView;
  return function () {
    var _defaultValue$values;
    switch (defaultValue.type) {
      case 'user':
        if (!((_defaultValue$values = defaultValue.values) !== null && _defaultValue$values !== void 0 && _defaultValue$values[0])) {
          return /*#__PURE__*/React.createElement(EmptyAvatar, null);
        }
    }
    return readView;
  };
};